/**
 * TypeScript projection of the Flow IR wire form.
 *
 * Source of truth (HUMAN-OWNED, do not edit): contracts/flow-ir/v1/flow-definition.v1.schema.json
 * + contracts/flow-ir/v1/flow-definition.v1.1.schema.json, .v1.2 and .v1.3 (all RATIFIED; v1.1 adds the
 * AI-node fields, v1.2 the fan-out, v1.3 its successOutcomes). These interfaces are a READ model for the canvas; they never widen or reinterpret the
 * contract. Fields optional here that the schema makes conditionally-required are validated by the
 * compiler, not the renderer (spec §7.1 — the compiler is the linter).
 */
import type { LifecycleStage, NodeKind } from '@/theme/tokens';

export type { LifecycleStage, NodeKind };

export type EdgeKind = 'sequence' | 'status' | 'data';

export interface FlowRef {
  id: string;
  version: number;
}

export interface FlowMetadata {
  name: string;
  description?: string;
  stages?: LifecycleStage[];
  jurisdictions?: string[];
  labels?: string[];
}

export interface ConfigurableRange {
  min: number;
  max: number;
}

export interface Idempotency {
  mode: 'NATURALLY_IDEMPOTENT' | 'IDEMPOTENT_BY_KEY' | 'AT_MOST_ONCE';
  key?: string;
}

export interface ScoringSource {
  source: string;
  weight: number;
}

/** v1.1 AI fields (llmTask / confidenceGate). */
export type PathType = 'ASSISTIVE' | 'ADVERSARIAL';

export interface FallbackSpec {
  mode: 'DETERMINISTIC_VALUE' | 'FAIL_CLOSED';
  value?: Record<string, unknown>;
}

export interface PromptSpec {
  id: string;
  template: string;
}

export interface LlmParams {
  envelopeId?: string;
  temperature?: number;
  maxOutputTokens?: number;
  citeOrShutUp?: boolean;
  shadow?: { envelopeId: string; promptId?: string; promptTemplate?: string };
  [k: string]: unknown;
}

/** Required on a fan-out, with no defaults (Flow IR v1.2). */
export interface FanOutGuardrails {
  maxFanOut?: number;
  maxDepth?: number;
  budget?: number;
  onLimit?: 'FAIL' | 'HUMAN_TASK';
}

export interface FlowNode {
  id: string;
  kind: NodeKind;
  name?: string;
  stage: LifecycleStage;

  // Governance surface (mixed in per node).
  locked?: boolean;
  configurable?: Record<string, ConfigurableRange>;
  obligations?: string[];
  skippable?: boolean;
  visibility?: 'ALL' | 'DATA_ROOM' | 'DASHBOARD';

  // capability
  capability?: string;
  config?: Record<string, unknown>;
  idempotency?: Idempotency;

  // decision
  expression?: string;
  outcomes?: string[];

  // join
  policy?: 'ALL' | 'ANY' | 'QUORUM';
  quorum?: number;

  // humanTask
  reviewerPool?: string;
  sla?: string;
  decisionOptions?: string[];
  makerChecker?: boolean;

  // subFlow
  flow?: FlowRef;
  entityBinding?: string;

  // dynamicBranch (v1.2): flow, entityBinding, policy and quorum as above
  over?: string;
  elementKey?: string;
  guardrails?: FanOutGuardrails;
  /** v1.3. Child outcomes that count as a success. Absent: all but REJECTED. */
  successOutcomes?: string[];

  // multiSourceScoring
  sources?: ScoringSource[];
  threshold?: number;

  // terminal
  outcome?: string;

  // llmTask (v1.1)
  pathType?: PathType;
  fallback?: FallbackSpec;
  prompt?: PromptSpec;
  params?: LlmParams;

  // confidenceGate (v1.1) — `source` is the upstream llmTask node id.
  source?: string;
}

export interface FlowEdge {
  id?: string;
  from: string;
  to: string;
  kind: EdgeKind;
  onStatus?: string; // kind=status
  guard?: string; // kind=sequence (CEL)
  binding?: { source: string; target: string }; // kind=data
  obligations?: string[];
}

export interface FlowDelta {
  nodeConfigOverrides?: Record<string, Record<string, unknown>>;
  nodeInsertions?: FlowNode[];
  nodeRemovals?: string[];
  edgeInsertions?: FlowEdge[];
  edgeRemovals?: string[];
  policyOverrides?: Record<string, unknown>;
}

export interface FlowDefinition {
  schema: 'flow-definition.v1';
  id: string;
  version: number;
  metadata: FlowMetadata;
  parent?: FlowRef;
  nodes?: FlowNode[];
  edges?: FlowEdge[];
  delta?: FlowDelta;
}

/** True when the definition is a variant (parent + delta) rather than a base (nodes). */
export function isVariant(def: FlowDefinition): boolean {
  return Boolean(def.parent && def.delta);
}

/** Deterministic edge id per the schema: `from->to#kind[:status]`. */
export function edgeId(e: FlowEdge): string {
  const base = `${e.from}->${e.to}#${e.kind}`;
  return e.id ?? (e.kind === 'status' && e.onStatus ? `${base}:${e.onStatus}` : base);
}

/** Control edges order execution; data edges never gate (spec §4.5). */
export function isControlEdge(e: FlowEdge): boolean {
  return e.kind === 'sequence' || e.kind === 'status';
}
